import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from 'sonner';
import {
    BarChart3, TrendingUp, TrendingDown, DollarSign, Download,
    RefreshCw, Loader2, Search, PieChart, Users, FileSpreadsheet,
    Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFinancialDashboardPage = () => {
    const { token } = useAdminAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [period, setPeriod] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetch(`${API}/admin/users`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                // Filter users who have transactions
                setUsers(result.users || []);
            }
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    }, [token]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API}/admin/financial-overview?period=${period}`;
            if (selectedUser !== 'all') {
                url += `&user_id=${selectedUser}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                toast.error('Failed to fetch financial data');
            }
        } catch (error) {
            console.error('Failed to fetch financial data:', error);
            toast.error('Failed to load financial overview');
        } finally {
            setLoading(false);
        }
    }, [token, period, selectedUser]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleExport = async () => {
        setExporting(true);
        try {
            let url = `${API}/admin/export-transactions?period=${period}`;
            if (selectedUser !== 'all') {
                url += `&user_id=${selectedUser}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `transactions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(downloadUrl);
                a.remove();
                toast.success('Transactions exported successfully');
            } else {
                toast.error('Export failed');
            }
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Failed to export transactions');
        } finally {
            setExporting(false);
        }
    };

    const filteredUsers = users.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.business_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="space-y-6" data-testid="admin-financial-dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Financial Overview</h1>
                    <p className="text-slate-400">Income & expense analysis across all users</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchData}
                        disabled={loading}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={exporting || !data?.summary?.transaction_count}
                        className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                        data-testid="export-transactions-btn"
                    >
                        {exporting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="w-4 h-4 mr-2" />
                        )}
                        Export to Excel
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm text-slate-400 mb-2 block">Time Period</label>
                            <Select value={period} onValueChange={setPeriod}>
                                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                                    <SelectValue placeholder="Select period" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Time</SelectItem>
                                    <SelectItem value="month">Last Month</SelectItem>
                                    <SelectItem value="quarter">Last Quarter</SelectItem>
                                    <SelectItem value="year">Last Year</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm text-slate-400 mb-2 block">Filter by User</label>
                            <Select value={selectedUser} onValueChange={setSelectedUser}>
                                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                                    <SelectValue placeholder="All Users" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Users</SelectItem>
                                    {filteredUsers.map(user => (
                                        <SelectItem key={user.user_id} value={user.user_id}>
                                            {user.business_name || user.name || user.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm text-slate-400 mb-2 block">Search Users</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name or email..."
                                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                                />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-10 h-10 animate-spin text-finmar-gold" />
                </div>
            ) : !data || data.summary?.transaction_count === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-16 text-center">
                        <FileSpreadsheet className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                        <h3 className="text-lg font-medium text-white mb-2">No Financial Data</h3>
                        <p className="text-slate-400">
                            {selectedUser !== 'all' 
                                ? 'This user has not uploaded any bank statements yet.'
                                : 'No users have uploaded bank statements yet.'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="bg-gradient-to-br from-green-900/40 to-green-800/40 border-green-700/50">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-green-400" />
                                    </div>
                                    <Badge variant="outline" className="border-green-500 text-green-400">
                                        <ArrowUpRight className="w-3 h-3 mr-1" />
                                        Income
                                    </Badge>
                                </div>
                                <p className="text-slate-400 text-sm">Total Income</p>
                                <p className="text-2xl font-bold text-white mt-1">
                                    {formatCurrency(data.summary.total_income)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-red-900/40 to-red-800/40 border-red-700/50">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                                        <TrendingDown className="w-6 h-6 text-red-400" />
                                    </div>
                                    <Badge variant="outline" className="border-red-500 text-red-400">
                                        <ArrowDownRight className="w-3 h-3 mr-1" />
                                        Expense
                                    </Badge>
                                </div>
                                <p className="text-slate-400 text-sm">Total Expenses</p>
                                <p className="text-2xl font-bold text-white mt-1">
                                    {formatCurrency(data.summary.total_expense)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className={`bg-gradient-to-br ${data.summary.net_position >= 0 ? 'from-blue-900/40 to-blue-800/40 border-blue-700/50' : 'from-orange-900/40 to-orange-800/40 border-orange-700/50'}`}>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div className={`w-12 h-12 ${data.summary.net_position >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'} rounded-xl flex items-center justify-center`}>
                                        <DollarSign className={`w-6 h-6 ${data.summary.net_position >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
                                    </div>
                                </div>
                                <p className="text-slate-400 text-sm">Net Position</p>
                                <p className={`text-2xl font-bold ${data.summary.net_position >= 0 ? 'text-blue-400' : 'text-orange-400'} mt-1`}>
                                    {formatCurrency(data.summary.net_position)}
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/40 border-purple-700/50">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                                        <PieChart className="w-6 h-6 text-purple-400" />
                                    </div>
                                </div>
                                <p className="text-slate-400 text-sm">Tax Deductible</p>
                                <p className="text-2xl font-bold text-purple-400 mt-1">
                                    {formatCurrency(data.summary.tax_deductible)}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-finmar-gold">{data.summary.transaction_count}</p>
                                <p className="text-sm text-slate-400">Total Transactions</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-white">{data.summary.users_with_data || 1}</p>
                                <p className="text-sm text-slate-400">Users with Data</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-green-400">{Object.keys(data.income_by_category || {}).length}</p>
                                <p className="text-sm text-slate-400">Income Categories</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4 text-center">
                                <p className="text-3xl font-bold text-red-400">{Object.keys(data.expense_by_category || {}).length}</p>
                                <p className="text-sm text-slate-400">Expense Categories</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Expense Breakdown */}
                        <Card className="bg-slate-800 border-slate-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-red-400" />
                                    Expense Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {Object.keys(data.expense_by_category || {}).length > 0 ? (
                                    <div className="space-y-4">
                                        {Object.entries(data.expense_by_category)
                                            .sort(([,a], [,b]) => b - a)
                                            .slice(0, 8)
                                            .map(([category, amount]) => {
                                                const maxExpense = Math.max(...Object.values(data.expense_by_category));
                                                const percentage = (amount / maxExpense) * 100;
                                                return (
                                                    <div key={category} className="space-y-1">
                                                        <div className="flex items-center justify-between text-sm">
                                                            <span className="text-slate-300 capitalize">
                                                                {category.replace(/_/g, ' ')}
                                                            </span>
                                                            <span className="text-white font-medium">
                                                                {formatCurrency(amount)}
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-700 rounded-full h-2">
                                                            <div
                                                                className="bg-gradient-to-r from-red-500 to-red-400 h-2 rounded-full transition-all duration-500"
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ) : (
                                    <p className="text-slate-400 text-center py-8">No expense data available</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Income Breakdown */}
                        <Card className="bg-slate-800 border-slate-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-green-400" />
                                    Income Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {Object.keys(data.income_by_category || {}).length > 0 ? (
                                    <div className="space-y-4">
                                        {Object.entries(data.income_by_category)
                                            .sort(([,a], [,b]) => b - a)
                                            .slice(0, 8)
                                            .map(([category, amount]) => {
                                                const maxIncome = Math.max(...Object.values(data.income_by_category));
                                                const percentage = (amount / maxIncome) * 100;
                                                return (
                                                    <div key={category} className="space-y-1">
                                                        <div className="flex items-center justify-between text-sm">
                                                            <span className="text-slate-300 capitalize">
                                                                {category.replace(/_/g, ' ')}
                                                            </span>
                                                            <span className="text-white font-medium">
                                                                {formatCurrency(amount)}
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-700 rounded-full h-2">
                                                            <div
                                                                className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full transition-all duration-500"
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ) : (
                                    <p className="text-slate-400 text-center py-8">No income data available</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Monthly Trend */}
                    {data.monthly_data && data.monthly_data.length > 0 && (
                        <Card className="bg-slate-800 border-slate-700">
                            <CardHeader>
                                <CardTitle className="text-white flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-finmar-gold" />
                                    Monthly Trend
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-end gap-3 h-48">
                                    {data.monthly_data.slice(-12).map((month) => {
                                        const maxVal = Math.max(...data.monthly_data.map(m => Math.max(m.income, m.expense)), 1);
                                        return (
                                            <div key={month.month} className="flex-1 flex flex-col items-center gap-2">
                                                <div className="w-full flex gap-1 items-end h-36">
                                                    <div
                                                        className="flex-1 bg-gradient-to-t from-green-600 to-green-400 rounded-t transition-all duration-500"
                                                        style={{ height: `${(month.income / maxVal) * 100}%` }}
                                                        title={`Income: ${formatCurrency(month.income)}`}
                                                    />
                                                    <div
                                                        className="flex-1 bg-gradient-to-t from-red-600 to-red-400 rounded-t transition-all duration-500"
                                                        style={{ height: `${(month.expense / maxVal) * 100}%` }}
                                                        title={`Expense: ${formatCurrency(month.expense)}`}
                                                    />
                                                </div>
                                                <span className="text-xs text-slate-400">
                                                    {month.month.slice(5)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center justify-center gap-8 mt-6 pt-4 border-t border-slate-700">
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-gradient-to-r from-green-600 to-green-400 rounded" />
                                        <span className="text-sm text-slate-400">Income</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 bg-gradient-to-r from-red-600 to-red-400 rounded" />
                                        <span className="text-sm text-slate-400">Expense</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminFinancialDashboardPage;
